import { useMemo, useState, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
  Blocks,
  Check,
  ExternalLink,
  Plus,
  Settings2,
  Shield,
  Trash2,
} from 'lucide-react';
import { appsApi } from '../../api/endpoints/apps';
import { Button } from '../../components/ui/Button';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import { Toggle } from '../../components/ui/Toggle';
import { Select } from '../../components/ui/Select';
import { SearchInput } from '../../components/ui/SearchInput';
import { useToast } from '../../components/ui/Alert';
import { scopeLabel, categoryLabel } from '../../lib/appScopes';
import { openAppManagePage } from '../../lib/appManage';
import type { StoreApp, SettingsSchemaField } from '../../types/app';

// Tracking pixels are managed in Settings → Tracking with a simpler UX,
// so we hide their app-store entries to avoid duplicate paths that do the same thing.
const HIDDEN_APP_IDS = new Set([
  'facebook-pixel',
  'tiktok-pixel',
  'snapchat-pixel',
  'google-analytics',
  'google-ads',
  'twitter-pixel',
]);

const ALL_CATEGORIES = '__all__';

/** Square action button for the installed rows, where four labelled buttons
 *  would push the row past its width the moment an app name gets long. */
function IconAction({
  label,
  onClick,
  tone = 'default',
  expanded,
  children,
}: {
  label: string;
  onClick: () => void;
  tone?: 'default' | 'danger';
  expanded?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      aria-expanded={expanded}
      className={clsx(
        'inline-flex h-9 items-center justify-center gap-1 rounded-xl border px-2.5 transition-colors',
        tone === 'danger'
          ? 'border-danger/25 bg-danger/10 text-danger hover:bg-danger/20'
          : 'border-border-default text-text-muted hover:border-border-hover hover:bg-bg-hover hover:text-text-primary',
        expanded && 'border-border-hover bg-bg-hover text-text-primary',
      )}
    >
      {children}
    </button>
  );
}

/** Granted scopes are read-only here: the merchant reviews them on the consent
 *  screen at install time, so the app page only ever reports what was granted. */
function grantedScopes(app: StoreApp): string[] {
  return app.scopes ?? app.permissions ?? [];
}

/** Only apps that actually declare settings (or already carry config) get a
 *  Settings button — the rest have nothing to show but an empty form. */
function hasSettings(app: StoreApp): boolean {
  if (app.settingsSchema && app.settingsSchema.length > 0) return true;
  return Object.keys(app.config ?? {}).length > 0;
}

export function AppsPage() {
  const { storeId } = useParams<{ storeId: string }>();
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>(ALL_CATEGORIES);
  // Inline uninstall confirmation, keyed by appId so only one row is armed.
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  // Which installed rows have their granted-permissions list expanded.
  const [openScopesId, setOpenScopesId] = useState<string | null>(null);
  const [pendingAppId, setPendingAppId] = useState<string | null>(null);

  // Settings modal state
  const [configApp, setConfigApp] = useState<StoreApp | null>(null);
  const [configValues, setConfigValues] = useState<Record<string, unknown>>({});

  const { data, isLoading } = useQuery({
    queryKey: ['apps', storeId],
    queryFn: () => appsApi.list(storeId!),
    enabled: !!storeId,
  });

  const installMutation = useMutation({
    mutationFn: (appId: string) => appsApi.install(storeId!, appId),
    onSuccess: (result) => {
      // External app: hand the merchant over to the consent screen. Nothing is
      // installed yet, so no success toast and no cache invalidation.
      if (result?.requiresAuthorization && result.authorizeUrl) {
        window.location.href = result.authorizeUrl;
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['apps', storeId] });
      toast('success', t('apps.installSuccess'));
    },
    onError: () => toast('error', t('common.error')),
    onSettled: () => setPendingAppId(null),
  });

  const uninstallMutation = useMutation({
    mutationFn: (appId: string) => appsApi.uninstall(storeId!, appId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apps', storeId] });
      toast('success', t('apps.uninstallSuccess'));
      setConfirmingId(null);
    },
    onError: () => toast('error', t('common.error')),
    onSettled: () => setPendingAppId(null),
  });

  const configMutation = useMutation({
    mutationFn: ({ appId, config }: { appId: string; config: Record<string, unknown> }) =>
      appsApi.updateConfig(storeId!, appId, config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apps', storeId] });
      toast('success', t('common.success'));
      setConfigApp(null);
    },
    onError: () => toast('error', t('common.error')),
  });

  const install = (appId: string) => {
    setPendingAppId(appId);
    installMutation.mutate(appId);
  };

  const uninstall = (appId: string) => {
    setPendingAppId(appId);
    uninstallMutation.mutate(appId);
  };

  const openConfig = (app: StoreApp) => {
    setConfigApp(app);
    if (app.settingsSchema && app.settingsSchema.length > 0) {
      // Initialize from existing config, falling back to schema defaults
      const values: Record<string, unknown> = {};
      for (const field of app.settingsSchema) {
        const existing = app.config?.[field.key];
        values[field.key] = existing !== undefined ? existing : (field.defaultValue ?? '');
      }
      setConfigValues(values);
    } else {
      const cfg = (app.config ?? {}) as Record<string, string>;
      setConfigValues({ ...cfg });
    }
  };

  const renderSchemaField = (field: SettingsSchemaField) => {
    const value = configValues[field.key];
    const hint = field.description || field.hint;

    switch (field.type) {
      case 'textarea':
        return (
          <div key={field.key}>
            <Textarea
              label={field.label}
              value={(value as string) ?? ''}
              placeholder={field.placeholder}
              required={field.required}
              rows={4}
              onChange={(e) =>
                setConfigValues((prev) => ({ ...prev, [field.key]: e.target.value }))
              }
            />
            {hint && <p className="text-xs text-text-muted mt-1">{hint}</p>}
          </div>
        );

      case 'number':
        return (
          <div key={field.key}>
            <Input
              label={field.label}
              type="number"
              value={value !== undefined && value !== null ? String(value) : ''}
              placeholder={field.placeholder}
              required={field.required}
              onChange={(e) =>
                setConfigValues((prev) => ({
                  ...prev,
                  [field.key]: e.target.value === '' ? '' : Number(e.target.value),
                }))
              }
            />
            {hint && <p className="text-xs text-text-muted mt-1">{hint}</p>}
          </div>
        );

      case 'checkbox':
      case 'boolean':
        return (
          <div key={field.key}>
            <Toggle
              label={field.label}
              description={hint}
              checked={Boolean(value)}
              onChange={(checked) =>
                setConfigValues((prev) => ({ ...prev, [field.key]: checked }))
              }
            />
          </div>
        );

      case 'select':
        return (
          <div key={field.key}>
            <Select
              label={field.label}
              value={(value as string) ?? ''}
              options={field.options ?? []}
              placeholder={field.placeholder}
              required={field.required}
              onChange={(e) =>
                setConfigValues((prev) => ({ ...prev, [field.key]: e.target.value }))
              }
            />
            {hint && <p className="text-xs text-text-muted mt-1">{hint}</p>}
          </div>
        );

      case 'text':
      default:
        return (
          <div key={field.key}>
            <Input
              label={field.label}
              type="text"
              value={(value as string) ?? ''}
              placeholder={field.placeholder}
              required={field.required}
              onChange={(e) =>
                setConfigValues((prev) => ({ ...prev, [field.key]: e.target.value }))
              }
            />
            {hint && <p className="text-xs text-text-muted mt-1">{hint}</p>}
          </div>
        );
    }
  };

  const openManage = async (app: StoreApp) => {
    try {
      const opened = await openAppManagePage(storeId!, app.appId, app.manageUrl);
      if (!opened) toast('error', t('apps.popupBlocked', 'Allow pop-ups to open the app.'));
    } catch {
      toast('error', t('common.error'));
    }
  };

  const renderAppLogo = (app: StoreApp, size: 'row' | 'card') => {
    const box = size === 'card' ? 'h-[46px] w-[46px] rounded-[13px]' : 'h-10 w-10 rounded-xl';
    const glyph = size === 'card' ? 'h-[22px] w-[22px]' : 'h-5 w-5';
    const imgSrc = app.icon || app.logoUrl;
    return (
      <div
        className={`flex ${box} shrink-0 items-center justify-center border border-border-default bg-bg-tertiary text-accent`}
      >
        {imgSrc ? (
          <img src={imgSrc} alt="" className={`${glyph} object-contain`} />
        ) : (
          <Blocks className={glyph} />
        )}
      </div>
    );
  };

  const apps = useMemo(
    () => (data?.apps ?? []).filter((a) => !HIDDEN_APP_IDS.has(a.appId)),
    [data],
  );
  const installed = apps.filter((a) => a.isInstalled);
  const available = apps.filter((a) => !a.isInstalled);

  const categories = useMemo(() => {
    const found = new Set(available.map((a) => a.category).filter(Boolean) as string[]);
    return [ALL_CATEGORIES, ...Array.from(found)];
  }, [available]);

  const filteredAvailable = available.filter((app) => {
    if (activeCategory !== ALL_CATEGORIES && app.category !== activeCategory) return false;
    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    return (
      app.name.toLowerCase().includes(q) ||
      (app.description ?? '').toLowerCase().includes(q) ||
      (app.category ?? '').toLowerCase().includes(q)
    );
  });

  if (isLoading) return <LoadingSpinner fullPage />;

  return (
    <div className="mx-auto max-w-[1080px]">
      {/* ── Page header ─────────────────────────────────────────────── */}
      <header className="mb-10 flex flex-col gap-1.5">
        <h1 className="text-2xl font-bold text-text-primary">{t('sidebar.apps')}</h1>
        <p className="text-sm leading-relaxed text-text-muted">
          {t('apps.pageSubtitle', 'Connect your store to the tools that grow your sales.')}
        </p>
      </header>

      {/* ── Installed ───────────────────────────────────────────────── */}
      <section className="mb-14">
        <div className="mb-4 flex items-center gap-2.5">
          <h2 className="text-lg font-semibold text-text-primary">{t('apps.installed')}</h2>
          <span className="num inline-flex h-6 min-w-6 items-center justify-center rounded-full border border-border-default bg-bg-tertiary px-2 text-xs font-semibold text-text-secondary">
            {installed.length}
          </span>
        </div>

        {installed.length === 0 ? (
          <div className="rounded-[20px] border border-dashed border-border-strong p-7 text-center text-sm text-text-muted">
            {t('apps.noInstalledHint', 'No apps installed yet. Browse the store below.')}
          </div>
        ) : (
          <div className="flex flex-col gap-3.5">
            {installed.map((app) => {
              const scopes = grantedScopes(app);
              const isConfirming = confirmingId === app.appId;
              const isPending = pendingAppId === app.appId;

              return (
                <div key={app._id} className="ui-card p-4">
                  {/* Identity and actions share one line on desktop and stack on
                      mobile, so a single installed app doesn't read as a poster
                      and twenty of them stay a scannable list. */}
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                    {renderAppLogo(app, 'row')}
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="truncate font-semibold text-text-primary">
                          {app.name}
                        </span>
                        <span className="inline-flex items-center gap-1 rounded-full bg-success/15 px-2 py-0.5 text-[11px] font-semibold text-success">
                          <Check className="h-3 w-3" />
                          {t('apps.installed')}
                        </span>
                      </div>
                      <p className="mt-0.5 truncate text-[13px] text-text-muted">
                        {app.author && <span dir="ltr">{app.author}</span>}
                        {app.author && app.description && <span className="mx-1.5">·</span>}
                        {app.description}
                      </p>
                    </div>

                    {isConfirming ? (
                      <div className="flex shrink-0 items-center gap-2">
                        <Button
                          variant="danger"
                          size="sm"
                          className="rounded-xl"
                          onClick={() => uninstall(app.appId)}
                          isLoading={isPending && uninstallMutation.isPending}
                        >
                          {t('apps.confirmUninstall', 'Confirm removal')}
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          className="rounded-xl"
                          onClick={() => setConfirmingId(null)}
                        >
                          {t('common.cancel')}
                        </Button>
                      </div>
                    ) : (
                      <div className="flex shrink-0 items-center gap-2">
                        {app.manageUrl && (
                          <Button
                            variant="primary"
                            size="sm"
                            className="rounded-xl"
                            onClick={() => openManage(app)}
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                            {t('apps.manage', 'Manage')}
                          </Button>
                        )}
                        {hasSettings(app) && (
                          <IconAction
                            label={t('apps.settings', 'Settings')}
                            onClick={() => openConfig(app)}
                          >
                            <Settings2 className="h-4 w-4" />
                          </IconAction>
                        )}
                        {scopes.length > 0 && (
                          <IconAction
                            label={t('apps.grantedScopes', 'Granted permissions')}
                            expanded={openScopesId === app.appId}
                            onClick={() =>
                              setOpenScopesId(openScopesId === app.appId ? null : app.appId)
                            }
                          >
                            <Shield className="h-4 w-4" />
                            <span className="num text-[11px] font-semibold">{scopes.length}</span>
                          </IconAction>
                        )}
                        <IconAction
                          label={t('apps.uninstall')}
                          tone="danger"
                          onClick={() => setConfirmingId(app.appId)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </IconAction>
                      </div>
                    )}
                  </div>

                  {isConfirming && (
                    <p className="mt-3 border-t border-border-default pt-3 text-[13px] font-medium text-danger">
                      {t('apps.confirmUninstallInline', {
                        app: app.name,
                        defaultValue: 'Remove "{{app}}"? The connection stops immediately.',
                      })}
                    </p>
                  )}

                  {!isConfirming && openScopesId === app.appId && (
                    <div className="mt-3 border-t border-border-default pt-3">
                      <div className="flex flex-wrap gap-1.5">
                        {scopes.map((scope) => (
                          <span
                            key={scope}
                            title={scope}
                            className="rounded-md bg-bg-tertiary px-2 py-1 text-xs text-text-secondary"
                          >
                            {scopeLabel(scope, t)}
                          </span>
                        ))}
                      </div>
                      <p className="mt-2 text-[11.5px] text-text-muted">
                        {t(
                          'apps.scopesReadOnly',
                          'Permissions are granted when you install the app.',
                        )}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ── App store ───────────────────────────────────────────────── */}
      <section>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
          <h2 className="text-lg font-semibold text-text-primary">{t('apps.appStore')}</h2>
          <SearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder={t('apps.searchApps', 'Search apps...')}
            className="w-full sm:w-[280px]"
          />
        </div>

        {/* One category for everything is not a filter, so the rail only shows
            up once the store actually spans more than one. */}
        {categories.length > 2 && (
          <div className="mb-5 flex flex-wrap gap-2">
            {categories.map((cat) => {
              const active = activeCategory === cat;
              return (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setActiveCategory(cat)}
                  className={
                    active
                      ? 'rounded-full border border-transparent bg-accent-action px-4 py-1.5 text-[13px] font-semibold text-white transition-colors'
                      : 'rounded-full border border-border-default px-4 py-1.5 text-[13px] font-semibold text-text-secondary transition-colors hover:border-border-hover hover:text-text-primary'
                  }
                >
                  {cat === ALL_CATEGORIES
                    ? t('apps.allCategories', 'All')
                    : categoryLabel(cat, t)}
                </button>
              );
            })}
          </div>
        )}

        {filteredAvailable.length === 0 ? (
          <div className="rounded-[20px] border border-dashed border-border-strong p-10 text-center text-sm text-text-muted">
            {searchQuery.trim()
              ? t('apps.noSearchResults', 'No app matches your search. Try another word.')
              : t('apps.noAvailable')}
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filteredAvailable.map((app) => (
              <div key={app._id} className="ui-card flex flex-col gap-3 p-5">
                <div className="flex items-center justify-between">
                  {renderAppLogo(app, 'card')}
                  {app.category && (
                    <span className="rounded-full border border-border-default px-2.5 py-1 text-[11.5px] text-text-muted">
                      {categoryLabel(app.category, t)}
                    </span>
                  )}
                </div>
                <div className="flex-1">
                  <div className="mb-1 text-base font-semibold text-text-primary">
                    {app.name}
                  </div>
                  {app.description && (
                    <p className="line-clamp-3 text-[13.5px] leading-[1.7] text-text-muted">
                      {app.description}
                    </p>
                  )}
                </div>
                <Button
                  size="sm"
                  className="w-full rounded-xl"
                  onClick={() => install(app.appId)}
                  isLoading={pendingAppId === app.appId && installMutation.isPending}
                >
                  <Plus className="h-3.5 w-3.5" />
                  {t('apps.install')}
                </Button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── App settings modal (schema-driven, or existing key-values) ── */}
      <Modal
        isOpen={!!configApp}
        onClose={() => setConfigApp(null)}
        title={`${t('apps.settings', 'Settings')} - ${configApp?.name}`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfigApp(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={() =>
                configApp && configMutation.mutate({ appId: configApp.appId, config: configValues })
              }
              isLoading={configMutation.isPending}
            >
              {t('common.save')}
            </Button>
          </>
        }
      >
        {configApp && (
          <div className="space-y-4">
            {configApp.settingsSchema && configApp.settingsSchema.length > 0
              ? configApp.settingsSchema.map((field) => renderSchemaField(field))
              : Object.entries(configValues).map(([key, value]) => (
                  <Input
                    key={key}
                    label={key}
                    value={value as string}
                    onChange={(e) =>
                      setConfigValues((prev) => ({ ...prev, [key]: e.target.value }))
                    }
                  />
                ))}
          </div>
        )}
      </Modal>
    </div>
  );
}
